// Popover dùng chung (N3): neo vào 1 phần tử, tự lật khi tràn màn hình (core/uiLuat.viTriPopover),
// đóng khi bấm ra ngoài / Esc / cuộn trang. Vẽ qua portal vào <body> ⇒ không bị vùng cuộn của bảng / modal cắt.
// LỒNG NHAU được (bước ghép G, thay KhungNoi của làn A): bấm / cuộn trong 1 popover khác (vd dropdown chọn nhiều mở từ bảng Lọc)
// không đóng popover cha; Esc chỉ đóng popover TRÊN CÙNG (ngăn xếp như Modal).
import { type ReactNode, type RefObject, useId, useLayoutEffect, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { viTriPopover } from "../core/uiLuat";

const nganXep: string[] = [];
const trongPop = (t: EventTarget | null) => !!(t as Element | null)?.closest?.(".pop");

export interface PopoverProps {
  mo: boolean;
  moc: RefObject<HTMLElement | null>;
  onDong: () => void;
  children: ReactNode;
  canPhai?: boolean;
  className?: string;
  /** Mặc định đóng khi bấm Esc. Tắt khi popover tự xử lý Esc (vd combobox). */
  escDong?: boolean;
  nhan?: string;
}

export function Popover({ mo, moc, onDong, children, canPhai, className, escDong = true, nhan }: PopoverProps) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  // onDong qua ref: màn truyền hàm mới mỗi lần vẽ không làm effect chạy lại (giữ đúng thứ tự ngăn xếp).
  const onDongRef = useRef(onDong);
  onDongRef.current = onDong;
  const [vt, setVt] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!mo) { setVt(null); return; }
    const datViTri = () => {
      const a = moc.current?.getBoundingClientRect();
      const p = ref.current;
      if (!a || !p) return;
      setVt(viTriPopover(a, p.offsetWidth, p.offsetHeight, window.innerWidth, window.innerHeight, canPhai));
    };
    datViTri();
    // Nội dung đổi cỡ (vd danh sách lọc ngắn lại) ⇒ đặt lại vị trí.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(datViTri) : null;
    if (ro && ref.current) ro.observe(ref.current);
    window.addEventListener("resize", datViTri);
    return () => { ro?.disconnect(); window.removeEventListener("resize", datViTri); };
  }, [mo, moc, canPhai]);

  useEffect(() => {
    if (!mo) return;
    const bamNgoai = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || moc.current?.contains(t) || trongPop(t)) return;
      onDongRef.current();
    };
    nganXep.push(id);
    const phim = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || nganXep[nganXep.length - 1] !== id) return;
      if (escDong) { e.stopPropagation(); onDongRef.current(); }
    };
    // Cuộn vùng NGOÀI popover ⇒ đóng (vị trí neo đã đổi). Cuộn bên trong danh sách của popover (kể cả popover con) thì giữ.
    // (E) Bỏ qua sự kiện cuộn trong 250 ms đầu: cuộn quán tính của bàn di chuột / cuộn tới nút ngay trước khi bấm vẫn bắn "scroll" SAU cú
    // bấm ⇒ popover vừa mở đã đóng (gặp ở bảng Kế hoạch mở lớp).
    const moLuc = performance.now();
    const cuon = (e: Event) => {
      if (performance.now() - moLuc < 250) return;
      if (!ref.current?.contains(e.target as Node) && !trongPop(e.target)) onDongRef.current();
    };
    document.addEventListener("pointerdown", bamNgoai, true);
    document.addEventListener("keydown", phim, true);
    window.addEventListener("scroll", cuon, true);
    return () => {
      document.removeEventListener("pointerdown", bamNgoai, true);
      document.removeEventListener("keydown", phim, true);
      window.removeEventListener("scroll", cuon, true);
      const i = nganXep.lastIndexOf(id);
      if (i >= 0) nganXep.splice(i, 1);
    };
  }, [mo, moc, escDong, id]);

  if (!mo) return null;
  return createPortal(
    <div
      ref={ref}
      className={"pop" + (className ? " " + className : "")}
      role="dialog"
      aria-label={nhan}
      // Lượt vẽ đầu chưa biết cỡ ⇒ ẩn để đo, tránh nháy ở góc trái trên.
      style={vt ? { top: vt.top, left: vt.left } : { top: 0, left: 0, visibility: "hidden" }}
    >
      {children}
    </div>,
    document.body,
  );
}
